import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, isApiError } from './api';
import { errorFields, errorMessage, messageForCode } from './api-error';
import { toast } from './toast';

/**
 * Katalog ucunun panel tarafındaki sözleşmesi: tipler, etiketler, sorgular.
 *
 * Burası `apps/api/src/modules/items/items.dto.ts`in AYNASIDIR. Sunucu
 * `whitelist: true` + `forbidNonWhitelisted: true` ile çalışır — DTO'da
 * olmayan tek bir alan bile isteği 400'e çevirir. Bu yüzden gövde tipleri
 * "yaklaşık" değil, birebir tutulur.
 *
 * ⚠ PARA VE ORAN `string`. Sunucu bunları JSON SAYISI olarak almaz ve panel de
 * hiçbir yerde `Number()`a sokmaz: `(1.005).toFixed(2) === '1.00'` sınıfı kuruş
 * kaybı, değer bir kez double'a çevrildiği anda olur. Girdi kutusundan çıkan
 * ham metin doğrudan gövdeye konur, sunucudaki tam sayı kuruş aritmetiği
 * (`money.ts`) onu kanonikleştirir.
 */

// ────────────────────────────────────────────────────────────── sabit kümeler

/**
 * İç birim kümesi. EN 16931 BT-130 UN/ECE Rec 20 kodu (HUR/DAY/MON/C62…)
 * sunucudaki `common/units.ts` eşlemesinde durur; panel yalnız iç değeri bilir.
 */
export const ITEM_UNITS = ['HOUR', 'DAY', 'MONTH', 'PIECE', 'PROJECT'] as const;
export type ItemUnit = (typeof ITEM_UNITS)[number];

export const UNIT_LABELS: Record<ItemUnit, string> = {
  HOUR: 'Saat',
  DAY: 'Gün',
  MONTH: 'Ay',
  PIECE: 'Adet',
  PROJECT: 'Proje',
};

export const SUPPLY_KINDS = ['SERVICE', 'GOODS'] as const;
export type SupplyKind = (typeof SUPPLY_KINDS)[number];

export const SUPPLY_KIND_LABELS: Record<SupplyKind, string> = {
  SERVICE: 'Hizmet',
  GOODS: 'Mal',
};

export const HARDWARE_DISPOSITIONS = ['NA', 'SOLD', 'RENTED'] as const;
export type HardwareDisposition = (typeof HARDWARE_DISPOSITIONS)[number];

export const HARDWARE_DISPOSITION_LABELS: Record<HardwareDisposition, string> = {
  NA: 'Donanım yok',
  SOLD: 'Satıldı — mülkiyet geçiyor',
  RENTED: 'Kiralandı — iade ediliyor',
};

/**
 * ⚠ `tr` BURADA VAR ama belgenin diline ASLA girmez: operatör kalemi Türkçe
 * adıyla arar, fatura Fransızca basılır. İki küme bilerek ayrıdır.
 */
export const ITEM_TEXT_LANGUAGES = ['fr', 'nl', 'en', 'tr'] as const;
export type ItemTextLanguage = (typeof ITEM_TEXT_LANGUAGES)[number];

/**
 * `fr` DIŞINDAKİLER. Ayrı bir küme çünkü Fransızcanın zorunluluğu hukukidir
 * ve formda ayrı bir yerde durur; ötekiler "isteğe bağlı" kutusundadır.
 */
export const ITEM_SECONDARY_LANGUAGES = ['nl', 'en', 'tr'] as const;
export type ItemSecondaryLanguage = (typeof ITEM_SECONDARY_LANGUAGES)[number];

export const ITEM_TEXT_LANGUAGE_LABELS: Record<ItemTextLanguage, string> = {
  fr: 'Fransızca',
  nl: 'Felemenkçe',
  en: 'İngilizce',
  tr: 'Türkçe (yalnız panel)',
};

/** Belçika'da geçerli oranlar. Serbest metin yerine liste: yazım hatası KDV hatasıdır. */
export const VAT_RATE_CHOICES = ['21', '12', '6', '0'] as const;

// ──────────────────────────────────────────────────────────────────── tipler

/** ⚠ `fr` ZORUNLU — Taaldecreet 19.07.1973 (şemada da: `CHK_items_name_fr`). */
export type ItemText = { fr: string } & Partial<Record<ItemTextLanguage, string>>;

export interface Item {
  id: string;
  code: string | null;
  name: ItemText;
  description: ItemText | null;
  unit: ItemUnit;
  /** ⚠ `decimal(14,2)` → TypeORM STRING döndürür. Ekranda `money()`den geçir. */
  unitPrice: string;
  /** ⚠ `decimal(5,2)` → yine STRING. Ekranda `percent()`ten geçir. */
  vatRate: string;
  supplyKind: SupplyKind;
  isEss: boolean;
  isImmovableWork: boolean;
  hardwareDisposition: HardwareDisposition;
  revenueAccountCode: string | null;
  defaultServicePeriodMonths: number | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ItemListResponse {
  items: Item[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListItemsParams {
  q?: string;
  active?: boolean;
  supplyKind?: SupplyKind;
  isEss?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Yazma gövdesi.
 *
 * ⚠ GÖNDERİLMEYEN ALAN "değişmedi" demektir; `undefined` bir anahtar gövdeye
 * HİÇ konmaz. Boş dize göndermek ayrı bir şeydir ve çoğu alanda 400'dür
 * (`@MinLength(1)`), bu yüzden form boş bıraktığı dili anahtar olarak da
 * yazmaz.
 */
export interface ItemWriteBody {
  code?: string | null;
  name?: ItemText;
  /** `null` = açıklamayı TEMİZLE. Anahtarı hiç göndermemek = dokunma. */
  description?: ItemText | null;
  unit?: ItemUnit;
  unitPrice?: string;
  vatRate?: string;
  supplyKind?: SupplyKind;
  isEss?: boolean;
  isImmovableWork?: boolean;
  hardwareDisposition?: HardwareDisposition;
  revenueAccountCode?: string | null;
  /**
   * ⚠ METİN gönderilir, sayı değil: DTO'da `@Type(() => Number)` var ve '12'yi
   * 12'ye çevirir. Panelde `Number()` çağırmamak, para alanlarındaki kuralın
   * yanlışlıkla delinmesini engelliyor — tek bir `Number()` alışkanlığı
   * yeterince yakın duran `unitPrice`a da sıçrar.
   */
  defaultServicePeriodMonths?: string | null;
  active?: boolean;
}

export type CreateItemBody = ItemWriteBody & { name: ItemText };

// ─────────────────────────────────────────────────────── sınır doğrulamaları

/** `decimal(14,2)`, negatif OLMAYAN para — `items.dto.ts#MONEY_PATTERN` ikizi. */
export const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/** `decimal(5,2)` yüzde, 0–100 — `items.dto.ts#PERCENT_PATTERN` ikizi. */
export const PERCENT_PATTERN = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/;

/** 1–120 ay; DTO'daki `@IsInt() @Min(1) @Max(120)` karşılığı. */
export const MONTHS_PATTERN = /^\d{1,3}$/;

/**
 * '21.00' → '21'. Sunucu oranı kanonikleştirip iki ondalıkla döndürür; seçim
 * listesinin değerleriyle eşleşmesi için sondaki sıfırlar METİN üzerinde
 * kırpılır. ⚠ `parseFloat` yok: bu değer bir sonraki kaydetmede aynen geri
 * gönderilecek, dolayısıyla hiçbir aşamada double'a uğramamalı.
 */
export function trimRateText(raw: string): string {
  if (!raw.includes('.')) return raw;
  const trimmed = raw.replace(/\.?0+$/, '');
  return trimmed === '' ? '0' : trimmed;
}

// ───────────────────────────────────────────────────────────── hata sözlüğü

/** Sunucunun iki alanı BİRLİKTE suçladığı çapraz KDV kuralları. */
const CROSS_RULE_CODES = new Set([
  'ITEM_ESS_REQUIRES_SERVICE',
  'ITEM_IMMOVABLE_REQUIRES_SERVICE',
  'ITEM_SOLD_REQUIRES_GOODS',
]);

/** Genel `VALIDATION_ERROR`da alan ALTINA yazılacak ipuçları. */
const FIELD_HINTS: Record<string, string> = {
  code: 'En fazla 64 karakter.',
  'name.fr': 'Fransızca ad zorunlu ve en fazla 500 karakter.',
  'name.nl': 'Doldurulacaksa boş bırakılamaz, en fazla 500 karakter.',
  'name.en': 'Doldurulacaksa boş bırakılamaz, en fazla 500 karakter.',
  'name.tr': 'Doldurulacaksa boş bırakılamaz, en fazla 500 karakter.',
  name: 'Fransızca ad zorunlu.',
  description: 'Açıklama girecekseniz Fransızcası zorunlu.',
  'description.fr': 'Açıklama girecekseniz Fransızcası zorunlu.',
  unitPrice: 'Nokta ile ve en fazla iki ondalık (örn. 1234.56).',
  vatRate: '0 ile 100 arasında bir oran.',
  revenueAccountCode: 'En fazla 10 karakter.',
  defaultServicePeriodMonths: '1 ile 120 arasında tam sayı ay.',
};

function apiCode(err: unknown): string | null {
  return isApiError(err) ? err.code : null;
}

/**
 * Operatöre gösterilecek cümle.
 *
 * Katalog kodları da dahil HER cümle `lib/api-error.ts`teki tek sözlükten
 * gelir; ham `err.message` hiçbir yolla ekrana çıkmaz (dil bağımsız değil ve
 * 5xx'te bilerek boş). Bu sarmalayıcı, çağrı noktaları alan diliyle okunsun
 * diye duruyor — kendi sözlüğü YOKTUR ve olmamalıdır.
 */
export function itemErrorMessage(err: unknown): string {
  return errorMessage(err);
}

/**
 * Toast. `toast.error` zaten tek sözlüğe bakar ve 500'lerde operatörün
 * destekle paylaşabileceği TEK şeyi — iz numarasını — mesajın altına ekler.
 */
export function toastItemError(err: unknown): void {
  toast.error(err);
}

/**
 * Sunucunun `applyBusinessRules`ının panel ikizi.
 *
 * ⚠ OTOMATİK DÜZELTME YOK ve bu bilinçli: "MAL seçildi, o hâlde elektronik
 * hizmet işaretini kaldırayım" demek, operatörün hiç görmediği bir KDV kararını
 * onun adına vermektir. Sunucu da aynı gerekçeyle düzeltmiyor — burada yalnız
 * AYNI cümle bir tur erken söyleniyor, kural tek yerde kalıyor.
 *
 * Sıra sunucudakiyle aynı (ESS → taşınmaz → satıldı) ki iki taraf aynı girdide
 * aynı hatayı göstersin.
 */
export function crossRuleViolation(draft: {
  supplyKind: SupplyKind;
  isEss: boolean;
  isImmovableWork: boolean;
  hardwareDisposition: HardwareDisposition;
}): { code: string; fields: string[]; message: string } | null {
  const rule = (code: string, fields: string[]) => ({
    code,
    fields,
    // Cümle sözlükten alınır: sunucu aynı kodu döndürdüğünde operatör AYNI
    // metni görsün, "iki farklı hata" sanmasın.
    message: messageForCode(code) ?? 'Alanların bileşimi geçerli değil.',
  });

  if (draft.isEss && draft.supplyKind !== 'SERVICE') {
    return rule('ITEM_ESS_REQUIRES_SERVICE', ['isEss', 'supplyKind']);
  }
  if (draft.isImmovableWork && draft.supplyKind !== 'SERVICE') {
    return rule('ITEM_IMMOVABLE_REQUIRES_SERVICE', ['isImmovableWork', 'supplyKind']);
  }
  if (draft.hardwareDisposition === 'SOLD' && draft.supplyKind !== 'GOODS') {
    return rule('ITEM_SOLD_REQUIRES_GOODS', ['hardwareDisposition', 'supplyKind']);
  }
  return null;
}

/**
 * Alan listesiyle gelen ama bir ALAN hatası olmayan, bir İŞ KAPSAMI kararı
 * olan kodlar. Donanım tedariki kapalıyken `isImmovableWork` işaretlemek
 * yanlış bir değer girmek değildir; bugün yapılmayan bir işi faturalamaya
 * kalkmaktır. `FIELD_HINTS`in "Bu alan geçersiz." cümlesi bu yüzden yanıltırdı.
 */
const SCOPE_RULE_CODES = new Set(['HARDWARE_SUPPLY_DISABLED']);

/** Alan → alan altına yazılacak metin. Boşsa o alan suçlanmamış demektir. */
export function itemFieldProblems(err: unknown): Map<string, string> {
  const code = apiCode(err);
  const fields = errorFields(err);
  const problems = new Map<string, string>();

  for (const field of fields) {
    if (code !== null && (CROSS_RULE_CODES.has(code) || SCOPE_RULE_CODES.has(code))) {
      // Çapraz kuralda iki alan da AYNI cümleyi taşır: hata ikisinin
      // BİLEŞİMİNDEDİR, tek başına hiçbiri yanlış değildir. Kapsam kapısında
      // alan yanlış değildir; kapıyı TETİKLEYEN alandır ve operatör
      // hangisi olduğunu görmelidir.
      problems.set(field, itemErrorMessage(err));
      continue;
    }
    problems.set(field, FIELD_HINTS[field] ?? 'Bu alan geçersiz.');
  }
  return problems;
}

// ──────────────────────────────────────────────────────────────── sorgular

/**
 * ⚠ Boolean süzgeçler METİN olarak gider ('true'/'false') ve sunucu onları
 * ELLE çevirir: `@Type(() => Boolean)` `Boolean('false') === true` yaptığı için
 * "pasifleri göster" sessizce "aktifleri göster"e dönerdi.
 */
function buildQuery(params: ListItemsParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.active !== undefined) search.set('active', String(params.active));
  if (params.supplyKind !== undefined) search.set('supplyKind', params.supplyKind);
  if (params.isEss !== undefined) search.set('isEss', String(params.isEss));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined && params.offset > 0) {
    search.set('offset', String(params.offset));
  }
  const qs = search.toString();
  return qs === '' ? '' : `?${qs}`;
}

/** Tüm katalog sorgularının ortak ön eki — geçersiz kılma bunun üstünden yapılır. */
export const ITEMS_KEY = ['items'] as const;

export function useItems(params: ListItemsParams) {
  return useQuery({
    queryKey: [...ITEMS_KEY, params],
    queryFn: ({ signal }) => api.get<ItemListResponse>(`/items${buildQuery(params)}`, { signal }),
    /*
     * Arama kutusuna yazarken tablo BOŞALMASIN: her tuşta liste kaybolup
     * "yükleniyor"a dönmesi, operatörün az önce gördüğü satırı bulmasını
     * zorlaştırır. Eski sayfa yeni sonuç gelene kadar durur.
     */
    placeholderData: keepPreviousData,
  });
}

function useItemsInvalidator() {
  const qc = useQueryClient();
  // Ön ek eşleşmesi: süzgeç/sayfa kombinasyonlarının HEPSİ tazelenir. Yalnız
  // görünen sorguyu tazelemek, süzgeç değişince eski sonucu geri getirirdi.
  return () => void qc.invalidateQueries({ queryKey: ITEMS_KEY });
}

export function useCreateItem() {
  const invalidate = useItemsInvalidator();
  return useMutation({
    mutationFn: (body: CreateItemBody) => api.post<Item>('/items', body),
    onSuccess: invalidate,
  });
}

export function useUpdateItem() {
  const invalidate = useItemsInvalidator();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ItemWriteBody }) =>
      api.patch<Item>(`/items/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteItem() {
  const invalidate = useItemsInvalidator();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true; id: string }>(`/items/${id}`),
    onSuccess: invalidate,
  });
}
